import React, { useCallback, useContext, useMemo } from 'react'
import { Cell, Column, SortingRule } from 'react-table'
import { useIntl } from 'react-intl'

import type { Profile } from 'features/profiles/types'
import { sortByDate, sortByInsensitiveString } from 'components/Table/utils'
import {
  DateCell,
  DeleteButtonCell,
  DownloadButtonCell,
  Table,
} from 'components/Table'
import {
  StyledProfileDevices,
  StyledProfileThumbnail,
  StyledTableInnerWrapper,
  StyledTableOuterWrapper,
} from './styles'
import { useAppDispatch, useAppSelector } from 'app/hooks'
import { Regular } from 'components/Typography'
import { deleteProfileThunk } from 'features/profiles/slice'
import { CurrentUserPermissionsContext } from 'features/auth/permissions/CurrentUserPermissionsContext'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import type { TableHeader } from 'components/Table/types'
import { ProfilesMobileBlock } from '../ProfilesMobileBlock'
import { profilesPageMessages } from 'features/profiles/messages'

interface ProfilesTableProps {
  data: Profile[]
  searchValue?: string
  headers: Record<string, TableHeader>
  defaultSortBy: SortingRule<Profile>
  sortBy: SortingRule<Profile>
  setSortBy: (sortBy: SortingRule<Profile>) => void
}

function ProfilesTable({
  data,
  searchValue,
  headers,
  defaultSortBy,
  sortBy,
  setSortBy,
}: ProfilesTableProps) {
  const { hasRootUserPermission } = useContext(CurrentUserPermissionsContext)
  const hiddenColumns = useMemo(
    () => (hasRootUserPermission ? [] : [headers.DeleteButton.id]),
    [hasRootUserPermission, headers.DeleteButton.id]
  )

  const ManufacturerCell = (cell: Cell) => <strong>{cell.value}</strong>

  const DevicesCell = (cell: Cell) => (
    <StyledProfileDevices>{cell.value}</StyledProfileDevices>
  )

  const ThumbnailCell = (cell: Cell) => (
    <StyledProfileThumbnail url={cell.value} alt="Profile thumbnail" />
  )

  const DownloadButton = (cell: Cell<Profile>) => {
    const profile = cell.row.original
    const { downloadLink, manufacturer, model } = profile
    const fileName = `${manufacturer}_${model}.tar.gz`

    return (
      <DownloadButtonCell
        key={downloadLink}
        url={downloadLink}
        fileName={fileName}
      />
    )
  }

  const DeleteButton = (cell: Cell<Profile>) => {
    const profile = cell.row.original
    const { id, manufacturer, model } = profile

    const { formatMessage } = useIntl()
    const dispatch = useAppDispatch()
    const rootDomainId = useAppSelector(currentRootDomainIdSelector)

    const handleDelete = useCallback(async () => {
      if (rootDomainId) {
        await dispatch(deleteProfileThunk(rootDomainId, id))
      }
    }, [dispatch, id, rootDomainId])

    return (
      <DeleteButtonCell
        title={formatMessage(profilesPageMessages.deleteProfileModalTitle)}
        handleConfirm={handleDelete}
        disabled={!rootDomainId}
      >
        <Regular>
          {formatMessage(profilesPageMessages.deleteProfileModalDescription, {
            model,
            manufacturer,
            br: <br />,
          })}
        </Regular>
      </DeleteButtonCell>
    )
  }

  const columns: readonly Column<Profile>[] = useMemo(
    () => [
      {
        id: headers.thumbnail.id,
        Header: headers.thumbnail.label,
        accessor: (row) => row.thumbnail,
        Cell: ThumbnailCell,
        disableSortBy: true,
        disableGlobalFilter: true,
        percentWidth: '7%',
      },
      {
        id: headers.manufacturer.id,
        Header: headers.manufacturer.label,
        accessor: (row) => row.manufacturer,
        Cell: ManufacturerCell,
        sortType: sortByInsensitiveString,
        percentWidth: '10%',
      },
      {
        id: headers.model.id,
        Header: headers.model.label,
        accessor: (row) => row.model,
        percentWidth: '18%',
      },
      {
        id: headers.deviceClass.id,
        Header: headers.deviceClass.label,
        accessor: (row) => row.deviceClass,
        Cell: DevicesCell,
        percentWidth: '18%',
      },
      {
        id: headers.uploaded.id,
        Header: headers.uploaded.label,
        accessor: (row) => row.uploaded,
        percentWidth: '17%',
        Cell: DateCell,
        sortType: sortByDate,
        disableGlobalFilter: true,
      },
      {
        id: headers.version.id,
        Header: headers.version.label,
        accessor: (row) => row.version,
        percentWidth: '5%',
        disableGlobalFilter: true,
      },
      {
        id: headers.downloadLink.id,
        Header: headers.downloadLink.label,
        accessor: (row) => row.downloadLink,
        Cell: DownloadButton,
        disableSortBy: true,
        disableGlobalFilter: true,
        percentWidth: '5%',
      },
      {
        id: headers.DeleteButton.id,
        Header: headers.DeleteButton.label,
        accessor: (row) => row.id,
        Cell: DeleteButton,
        disableSortBy: true,
        disableGlobalFilter: true,
        percentWidth: '5%',
      },
    ],
    [headers]
  )

  return (
    <StyledTableOuterWrapper>
      <StyledTableInnerWrapper>
        <Table
          columns={columns}
          data={data}
          searchValue={searchValue}
          defaultSortBy={defaultSortBy}
          hiddenColumns={hiddenColumns}
          sortBy={sortBy}
          setSortBy={setSortBy}
          mobileViewComponent={ProfilesMobileBlock}
        />
      </StyledTableInnerWrapper>
    </StyledTableOuterWrapper>
  )
}

export default ProfilesTable
